import { useEffect, useRef, useState } from "react";
import { AttachButton, MediaInput } from "@/components/MediaInput";
import {
  Plus, Trash2, Send, Save, FolderOpen, Pencil, Bold, Italic, Underline, Strikethrough, Code, EyeOff, Quote, Link2, Heading, List,
  Hash, AtSign, Smile, Copy, FolderInput, ChevronUp, ChevronDown, LayoutTemplate, Image as ImageIcon, User, Type, Columns3, PanelBottom, MousePointerClick, Sparkles, Loader2, RotateCcw,
} from "lucide-react";
import { toast } from "sonner";
import { botPost, botPatch, templates, apiError } from "@/lib/api";
import { useData } from "@/context/DataContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChannelSelect, RoleSelect } from "@/components/pickers";
import { DiscordEmbedPreview } from "@/components/DiscordEmbedPreview";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { PageHeader, inputC, labelC, outlineBtn, primaryBtn } from "@/components/kit";
import { FANCY_STYLES, toFancy, toPlain } from "@/lib/fancyText";
import EmbedImporter from "@/pages/embed/EmbedImporter";

const emptyEmbed = (patch = {}) => ({
  title: "", description: "", color: "#5865F2", url: "", timestamp: false,
  author: { name: "", iconUrl: "" }, footer: { text: "", iconUrl: "" }, image: "", thumbnail: "", fields: [], ...patch,
});

const SWATCHES = ["#5865F2", "#8B5CF6", "#EB459E", "#ED4245", "#F47B67", "#FEE75C", "#57F287", "#1ABC9C", "#00A8FC", "#FFFFFF", "#2B2D31"];
const EMOJIS = ["✅", "❌", "⚠️", "📌", "📢", "🎉", "🔥", "⭐", "💎", "🎁", "🏆", "📅", "⏰", "🔗", "📜", "🛡️", "🎮", "🎵", "💬", "👋", "❤️", "👉", "➡️", "🔹"];
const LIMITS = { title: 256, description: 4096, fieldName: 256, fieldValue: 1024, footer: 2048, author: 256, content: 2000, total: 6000 };

// Modelli pronti da cui partire.
const PRESETS = [
  { id: "rules", icon: "📜", name: "Regolamento", msg: { content: "", embeds: [emptyEmbed({ title: "📜 Regolamento del server", color: "#ED4245", description: "Benvenuto! Per stare bene tutti insieme rispetta queste regole:", fields: [
    { name: "1 · Rispetto", value: "Niente insulti, discriminazioni o provocazioni.", inline: false },
    { name: "2 · Niente spam", value: "Evita messaggi ripetuti, tag inutili e pubblicità.", inline: false },
    { name: "3 · Canali giusti", value: "Scrivi ogni cosa nel canale dedicato.", inline: false },
    { name: "4 · Contenuti", value: "Niente contenuti NSFW o illegali.", inline: false },
  ], footer: { text: "Lo staff può intervenire in qualsiasi momento", iconUrl: "" } })], buttons: [] } },
  { id: "announce", icon: "📢", name: "Annuncio", msg: { content: "@everyone", embeds: [emptyEmbed({ title: "📢 Novità in arrivo!", color: "#5865F2", description: "Scrivi qui il tuo annuncio.\n\n**Cosa cambia**\n- Primo punto\n- Secondo punto", timestamp: true })], buttons: [] } },
  { id: "event", icon: "📅", name: "Evento", msg: { content: "", embeds: [emptyEmbed({ title: "🎉 Serata giochi", color: "#EB459E", description: "Unisciti a noi per una serata in compagnia!", fields: [
    { name: "📅 Quando", value: "Sabato alle 21:00", inline: true }, { name: "📍 Dove", value: "Canale vocale Generale", inline: true }, { name: "🎁 Premi", value: "Ruolo speciale al vincitore", inline: true },
  ] })], buttons: [] } },
  { id: "welcome", icon: "👋", name: "Info per i nuovi", msg: { content: "", embeds: [emptyEmbed({ title: "👋 Benvenuto!", color: "#57F287", description: "Ecco come iniziare:\n\n> **1.** Leggi le regole\n> **2.** Presentati\n> **3.** Scegli i tuoi ruoli" })], buttons: [] } },
  { id: "links", icon: "🔗", name: "Link e social", msg: { content: "", embeds: [emptyEmbed({ title: "🔗 Seguici anche qui", color: "#00A8FC", description: "Tutti i nostri canali ufficiali:" })], buttons: [
    { label: "YouTube", emoji: "📺", action: "link", style: "secondary", url: "https://youtube.com", roleId: "", customId: "" },
    { label: "Instagram", emoji: "📸", action: "link", style: "secondary", url: "https://instagram.com", roleId: "", customId: "" },
  ] } },
  { id: "staff", icon: "🛡️", name: "Cerchiamo staff", msg: { content: "", embeds: [emptyEmbed({ title: "🛡️ Candidature staff aperte", color: "#FEE75C", description: "Vuoi aiutarci a gestire il server?", fields: [
    { name: "Requisiti", value: "- Almeno 16 anni\n- Attivo nel server\n- Tanta pazienza", inline: false },
  ] })], buttons: [{ label: "Candidati", emoji: "📝", action: "ticket", style: "success", url: "", roleId: "", customId: "" }] } },
];

// Calcola la lunghezza totale come fa Discord (limite 6000 caratteri per messaggio).
const embedChars = (e) => (e.title || "").length + (e.description || "").length + (e.author?.name || "").length + (e.footer?.text || "").length
  + (e.fields || []).reduce((n, f) => n + (f.name || "").length + (f.value || "").length, 0);

function Counter({ value = "", max }) {
  const n = value.length;
  if (n < max * 0.8) return null;
  return <span className={`text-[11px] font-semibold ${n > max ? "text-[#F23F43]" : "text-[#FEE75C]"}`}>{n}/{max}</span>;
}

// Barra di formattazione: avvolge il testo selezionato con la sintassi di Discord.
function MdToolbar({ areaRef, value, onChange, compact }) {
  const { channels, roles } = useData();
  const [picker, setPicker] = useState(null);
  const [fontText, setFontText] = useState("");
  const selRef = useRef([0, 0]);
  const openFonts = () => {
    if (picker === "font") return setPicker(null);
    const el = areaRef.current;
    const s = el?.selectionStart ?? value.length, e = el?.selectionEnd ?? value.length;
    selRef.current = [s, e];
    setFontText(toPlain(value.slice(s, e)));
    setPicker("font");
  };
  /** Mette il testo trasformato al posto della selezione (o dove c'è il cursore). */
  const putFont = (text) => {
    const [s, e] = selRef.current;
    onChange(value.slice(0, s) + text + value.slice(e));
    setPicker(null);
    const el = areaRef.current;
    requestAnimationFrame(() => { el?.focus(); el?.setSelectionRange(s, s + text.length); });
  };
  const apply = (before, after = before, placeholder = "testo") => {
    const el = areaRef.current; if (!el) return;
    const s = el.selectionStart ?? value.length; const e = el.selectionEnd ?? value.length;
    const sel = value.slice(s, e) || placeholder;
    onChange(value.slice(0, s) + before + sel + after + value.slice(e));
    requestAnimationFrame(() => { el.focus(); el.setSelectionRange(s + before.length, s + before.length + sel.length); });
  };
  const insert = (text) => {
    const el = areaRef.current; const s = el?.selectionStart ?? value.length; const e = el?.selectionEnd ?? value.length;
    onChange(value.slice(0, s) + text + value.slice(e)); setPicker(null);
    requestAnimationFrame(() => { el?.focus(); el?.setSelectionRange(s + text.length, s + text.length); });
  };
  const linePrefix = (p) => {
    const el = areaRef.current; const s = el?.selectionStart ?? 0;
    const start = value.lastIndexOf("\n", s - 1) + 1;
    onChange(value.slice(0, start) + p + value.slice(start));
    requestAnimationFrame(() => el?.focus());
  };
  const B = ({ icon: Icon, title, onClick, active }) => (
    <button type="button" title={title} onMouseDown={(e) => e.preventDefault()} onClick={onClick}
      className={`flex h-7 w-7 items-center justify-center rounded-md transition-colors ${active ? "bg-[#5865F2] text-white" : "text-[#949BA4] hover:bg-white/5 hover:text-white"}`}><Icon className="h-3.5 w-3.5" /></button>
  );
  return (
    <div className="relative">
      <div className="flex flex-wrap items-center gap-0.5 rounded-t-md border border-b-0 border-[#232838] bg-[#0f111a] px-1 py-1">
        <B icon={Bold} title="Grassetto" onClick={() => apply("**")} />
        <B icon={Italic} title="Corsivo" onClick={() => apply("*")} />
        <B icon={Underline} title="Sottolineato" onClick={() => apply("__")} />
        <B icon={Strikethrough} title="Barrato" onClick={() => apply("~~")} />
        <B icon={EyeOff} title="Spoiler" onClick={() => apply("||")} />
        <B icon={Code} title="Codice" onClick={() => apply("`")} />
        {!compact && <>
          <span className="mx-1 h-4 w-px bg-[#232838]" />
          <B icon={Heading} title="Titolo grande" onClick={() => linePrefix("## ")} />
          <B icon={Quote} title="Citazione" onClick={() => linePrefix("> ")} />
          <B icon={List} title="Elenco puntato" onClick={() => linePrefix("- ")} />
        </>}
        <B icon={Link2} title="Link" onClick={() => apply("[", "](https://)", "testo del link")} />
        <span className="mx-1 h-4 w-px bg-[#232838]" />
        <B icon={Hash} title="Menziona un canale" active={picker === "ch"} onClick={() => setPicker(picker === "ch" ? null : "ch")} />
        <B icon={AtSign} title="Menziona un ruolo" active={picker === "role"} onClick={() => setPicker(picker === "role" ? null : "role")} />
        <B icon={Smile} title="Emoji" active={picker === "emoji"} onClick={() => setPicker(picker === "emoji" ? null : "emoji")} />
        <AttachButton onInsert={(url) => insert(`${value && !value.endsWith("\n") ? "\n" : ""}${url}`)} />
        <span className="mx-1 h-4 w-px bg-[#232838]" />
        <button type="button" title="Font (stili come su Instagram: 𝓐 𝔄 𝔸 Ⓐ…)" onMouseDown={(e) => e.preventDefault()} onClick={openFonts} data-testid="md-fonts"
          className={`flex h-7 items-center gap-1 rounded-md px-2 text-xs font-bold transition-colors ${picker === "font" ? "bg-[#5865F2] text-white" : "text-[#c7cbfb] hover:bg-white/5 hover:text-white"}`}>
          <span className="text-sm">𝓐𝓪</span>Font
        </button>
      </div>
      {picker && (
        <div className={`absolute left-0 right-0 top-full z-30 mt-1 ${picker === "font" ? "max-h-[420px]" : "max-h-56"} overflow-y-auto rounded-lg border border-[#232838] bg-[#161923] p-1.5 shadow-2xl`}>
          {picker === "font" && (
            <div className="space-y-2 p-1">
              <input autoFocus value={fontText} onChange={(e) => setFontText(e.target.value)} placeholder="Scrivi qui il testo (o selezionalo prima nel campo)"
                className="h-9 w-full rounded-md border border-[#232838] bg-[#0f111a] px-2.5 text-sm text-white outline-none placeholder:text-[#5f6479] focus:border-[#5865F2]" />
              <p className="px-0.5 text-[11px] text-[#949BA4]">Clicca uno stile: {selRef.current[0] !== selRef.current[1] ? "sostituisce il testo selezionato" : "viene inserito dove c'è il cursore"}. Menzioni, canali, link e {"{variabili}"} restano normali.</p>
              <div className="grid gap-1 sm:grid-cols-2">
                {FANCY_STYLES.map((st) => (
                  <button key={st.key} type="button" disabled={!fontText.trim()} onClick={() => putFont(toFancy(fontText, st.key))}
                    className="min-w-0 rounded-md border border-[#232838] bg-[#131620] px-2.5 py-1.5 text-left hover:border-[#5865F2]/60 hover:bg-[#5865F2]/10 disabled:cursor-not-allowed">
                    <span className="block truncate text-[15px] text-white">{toFancy(fontText.trim() ? fontText : st.name, st.key)}</span>
                    <span className="block text-[10px] text-[#6b7185]">{st.name}</span>
                  </button>
                ))}
              </div>
              {selRef.current[0] !== selRef.current[1] && (
                <button type="button" onClick={() => putFont(toPlain(value.slice(...selRef.current)))} className="w-full rounded-md border border-dashed border-[#232838] py-1.5 text-xs font-semibold text-[#B5BAC1] hover:text-white">Aa Riporta il testo selezionato a normale</button>
              )}
            </div>
          )}
          {picker === "emoji" && <div className="grid grid-cols-8 gap-1">{EMOJIS.map((e) => <button key={e} type="button" onClick={() => insert(e)} className="rounded p-1 text-lg hover:bg-white/5">{e}</button>)}</div>}
          {picker === "ch" && channels.filter((c) => c.type !== "category").map((c) => <button key={c.id} type="button" onClick={() => insert(`<#${c.id}>`)} className="block w-full rounded px-2 py-1.5 text-left text-sm text-[#DBDEE1] hover:bg-white/5"># {c.name}</button>)}
          {picker === "role" && [{ id: "everyone", name: "everyone", color: "#949BA4" }, ...roles.filter((r) => !r.isEveryone)].map((r) => (
            <button key={r.id} type="button" onClick={() => insert(r.id === "everyone" ? "@everyone" : `<@&${r.id}>`)} className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm text-[#DBDEE1] hover:bg-white/5">
              <span className="h-2.5 w-2.5 rounded-full" style={{ background: r.color }} />@{r.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function RichText({ value, onChange, rows = 3, placeholder, max, testId, compact }) {
  const ref = useRef(null);
  return (
    <div>
      <MdToolbar areaRef={ref} value={value || ""} onChange={onChange} compact={compact} />
      <Textarea ref={ref} value={value || ""} onChange={(e) => onChange(e.target.value)} rows={rows} placeholder={placeholder} data-testid={testId}
        className={`${inputC} rounded-t-none`} />
      {max && <div className="mt-1 flex justify-end"><Counter value={value || ""} max={max} /></div>}
    </div>
  );
}

function Section({ icon: Icon, title, open, onToggle, children, badge }) {
  return (
    <div className="rounded-xl border border-[#232838] bg-[#131620]/60">
      <button type="button" onClick={onToggle} className="flex w-full items-center gap-2 px-3 py-2.5 text-left">
        <Icon className="h-4 w-4 text-[#8ea0ff]" />
        <span className="flex-1 text-sm font-semibold text-white">{title}</span>
        {badge && <span className="rounded-full bg-[#232838] px-2 text-[11px] text-[#B5BAC1]">{badge}</span>}
        {open ? <ChevronUp className="h-4 w-4 text-[#949BA4]" /> : <ChevronDown className="h-4 w-4 text-[#949BA4]" />}
      </button>
      {open && <div className="space-y-3 border-t border-[#232838] p-3">{children}</div>}
    </div>
  );
}

function Lbl({ children, extra }) {
  return <div className="mb-1.5 flex items-center justify-between"><span className={`${labelC} mb-0`}>{children}</span>{extra}</div>;
}

export default function EmbedBuilder() {
  const { meta, channelName, guild } = useData();
  const [content, setContent] = useState("");
  const [embeds, setEmbeds] = useState([emptyEmbed()]);
  const [buttons, setButtons] = useState([]);
  const [channelId, setChannelId] = useState("");
  const [tmpls, setTmpls] = useState([]);
  const [saveOpen, setSaveOpen] = useState(false);
  const [loadOpen, setLoadOpen] = useState(false);
  const [tmplName, setTmplName] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [messageId, setMessageId] = useState("");
  const [link, setLink] = useState("");
  const [sending, setSending] = useState(false);
  const [openSec, setOpenSec] = useState({ "0-body": true });
  const [importOpen, setImportOpen] = useState(false);
  // I video non possono stare negli embed: vanno nel testo del messaggio (Discord li mostra con il player).
  const addVideo = (url) => setContent((c) => `${c ? `${c}\n` : ""}${url}`);

  const message = { content: content || undefined, embeds, buttons };
  const total = embeds.reduce((n, e) => n + embedChars(e), 0);
  const buttonStyles = meta?.buttonStyles || [{ value: "primary", label: "Blu" }, { value: "secondary", label: "Grigio" }, { value: "success", label: "Verde" }, { value: "danger", label: "Rosso" }];
  const buttonActions = meta?.buttonActions || [{ value: "link", label: "Apre un link" }, { value: "role", label: "Dà/toglie un ruolo" }, { value: "ticket", label: "Apre un ticket" }, { value: "custom", label: "Personalizzato" }];

  const loadTmpls = async () => { try { setTmpls(await templates.list()); } catch { /* elenco non disponibile */ } };
  useEffect(() => { loadTmpls(); }, []);

  const isOpen = (i, s) => !!openSec[`${i}-${s}`];
  const toggleSec = (i, s) => setOpenSec((o) => ({ ...o, [`${i}-${s}`]: !o[`${i}-${s}`] }));
  const setEmbed = (i, patch) => setEmbeds((arr) => arr.map((e, j) => (j === i ? { ...e, ...patch } : e)));
  const setField = (ei, fi, patch) => setEmbed(ei, { fields: embeds[ei].fields.map((f, j) => (j === fi ? { ...f, ...patch } : f)) });
  const moveEmbed = (i, d) => setEmbeds((arr) => { const a = [...arr]; const [m] = a.splice(i, 1); a.splice(i + d, 0, m); return a; });
  const setBtn = (i, patch) => setButtons((arr) => arr.map((x, j) => (j === i ? { ...x, ...patch } : x)));

  const loadMessage = (m) => {
    setContent(m.content || ""); setEmbeds(m.embeds?.length ? m.embeds.map((e) => emptyEmbed(e)) : []); setButtons(m.buttons || []);
    setOpenSec({ "0-body": true });
  };
  const reset = () => { loadMessage({ embeds: [emptyEmbed()] }); setLink(""); };

  // Incollando il link di un messaggio (tasto destro → Copia link) si ricavano canale e ID.
  const onLink = (v) => {
    setLink(v);
    const m = v.match(/channels\/\d+\/(\d+)\/(\d+)/);
    if (m) { setChannelId(m[1]); setMessageId(m[2]); }
  };

  const send = async () => {
    if (!channelId) return toast.error("Scegli il canale");
    if (!content && !embeds.length) return toast.error("Il messaggio è vuoto");
    if (total > LIMITS.total) return toast.error(`Troppo lungo: ${total}/${LIMITS.total} caratteri negli embed`);
    setSending(true);
    try {
      if (editMode) {
        if (!messageId) return toast.error("Incolla il link del messaggio da modificare");
        await botPatch(`channels/${channelId}/messages/${messageId}`, message);
        toast.success("Messaggio modificato");
      } else {
        const res = await botPost(`channels/${channelId}/messages`, message);
        toast.success("Messaggio inviato", { description: `In #${channelName(channelId)}` });
        if (res?.id) setMessageId(res.id);
      }
    } catch (e) { toast.error(apiError(e)); } finally { setSending(false); }
  };

  const doSave = async () => {
    try {
      await templates.save({ name: tmplName || "Modello", payload: { content, embeds, buttons } });
      toast.success("Modello salvato"); setSaveOpen(false); setTmplName(""); loadTmpls();
    } catch (e) { toast.error(apiError(e)); }
  };
  const applyTmpl = (t) => { loadMessage(t.payload || {}); setLoadOpen(false); toast.success(`Modello «${t.name}» caricato`); };
  const copyJson = async () => {
    try { await navigator.clipboard.writeText(JSON.stringify({ content, embeds, buttons }, null, 2)); toast.success("JSON copiato"); }
    catch { toast.error("Copia non riuscita"); }
  };

  return (
    <div className="space-y-6 pb-10">
      <EmbedImporter open={importOpen} onOpenChange={setImportOpen} onTemplatesSaved={loadTmpls}
        onOpenInEditor={(msg, chId) => { loadMessage(msg); setChannelId(chId); setEditMode(false); }} />
      <PageHeader icon={LayoutTemplate} title="Embed Builder" subtitle="Crea messaggi curati per il tuo server e guardali esattamente come appariranno su Discord."
        actions={<>
          <Button onClick={() => setImportOpen(true)} data-testid="import-embeds-btn" className="bg-gradient-to-r from-[#5865F2] to-[#8B5CF6] font-semibold text-white hover:opacity-90"><FolderInput className="mr-2 h-4 w-4" />Importa da MEE6</Button>
          <Button variant="outline" onClick={() => setLoadOpen(true)} data-testid="load-template-btn" className={outlineBtn}><FolderOpen className="mr-2 h-4 w-4" />I miei modelli</Button>
          <Button variant="outline" onClick={() => setSaveOpen(true)} data-testid="save-template-btn" className={outlineBtn}><Save className="mr-2 h-4 w-4" />Salva modello</Button>
        </>} />

      {/* Modelli pronti */}
      <div>
        <p className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#6b7185]"><Sparkles className="h-3.5 w-3.5" />Parti da un modello</p>
        <div className="flex gap-2 overflow-x-auto pb-1">
          {PRESETS.map((p) => (
            <button key={p.id} type="button" onClick={() => { loadMessage(structuredClone(p.msg)); toast.success(`Modello «${p.name}» caricato`); }} data-testid={`preset-${p.id}`}
              className="flex shrink-0 items-center gap-2 rounded-xl border border-[#232838] bg-[#161923] px-3.5 py-2.5 text-sm font-semibold text-[#DBDEE1] transition-all hover:-translate-y-0.5 hover:border-[#5865F2]/60 hover:text-white">
              <span className="text-lg">{p.icon}</span>{p.name}
            </button>
          ))}
          <button type="button" onClick={reset} className="flex shrink-0 items-center gap-2 rounded-xl border border-dashed border-[#232838] px-3.5 py-2.5 text-sm font-semibold text-[#949BA4] hover:text-white"><RotateCcw className="h-4 w-4" />Vuoto</button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,520px)]">
        {/* ── Editor ── */}
        <div className="min-w-0 space-y-4">
          <div className="card-elevated p-4">
            <Lbl extra={<Counter value={content} max={LIMITS.content} />}>Testo del messaggio</Lbl>
            <RichText value={content} onChange={setContent} rows={2} placeholder="Testo normale sopra gli embed (facoltativo). Qui puoi menzionare @everyone o un ruolo." testId="message-content-input" />
          </div>

          {embeds.map((em, i) => (
            <div key={i} className="card-elevated overflow-hidden" data-testid={`embed-editor-${i}`}>
              <div className="flex items-center gap-2 border-b border-[#232838] px-4 py-3" style={{ boxShadow: `inset 4px 0 0 ${em.color}` }}>
                <span className="flex-1 truncate text-sm font-bold text-white">Embed {i + 1}{em.title ? <span className="font-normal text-[#949BA4]"> · {em.title}</span> : ""}</span>
                <Button size="icon" variant="ghost" disabled={i === 0} onClick={() => moveEmbed(i, -1)} title="Sposta su" className="h-8 w-8 text-[#949BA4] hover:bg-white/5"><ChevronUp className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" disabled={i === embeds.length - 1} onClick={() => moveEmbed(i, 1)} title="Sposta giù" className="h-8 w-8 text-[#949BA4] hover:bg-white/5"><ChevronDown className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" disabled={embeds.length >= 10} onClick={() => setEmbeds((a) => [...a.slice(0, i + 1), structuredClone(em), ...a.slice(i + 1)])} title="Duplica" className="h-8 w-8 text-[#949BA4] hover:bg-white/5"><Copy className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" onClick={() => setEmbeds(embeds.filter((_, j) => j !== i))} title="Elimina embed" className="h-8 w-8 text-[#F23F43] hover:bg-[#F23F43]/10"><Trash2 className="h-4 w-4" /></Button>
              </div>
              <div className="space-y-3 p-4">
                {/* colore */}
                <div className="flex flex-wrap items-center gap-1.5">
                  {SWATCHES.map((c) => (
                    <button key={c} type="button" title={c} onClick={() => setEmbed(i, { color: c })}
                      className={`h-6 w-6 rounded-full border-2 transition-transform hover:scale-110 ${em.color?.toLowerCase() === c.toLowerCase() ? "border-white" : "border-transparent"}`} style={{ background: c }} />
                  ))}
                  <label className="relative ml-1 flex h-6 cursor-pointer items-center gap-1 rounded-full border border-[#232838] px-2 text-[11px] font-semibold text-[#B5BAC1] hover:text-white" title="Colore personalizzato">
                    <input type="color" value={/^#[0-9a-fA-F]{6}$/.test(em.color) ? em.color : "#5865F2"} onChange={(e) => setEmbed(i, { color: e.target.value })} data-testid="embed-color-picker" className="absolute inset-0 cursor-pointer opacity-0" />
                    <span className="h-3 w-3 rounded-full" style={{ background: em.color }} />{em.color}
                  </label>
                </div>

                <Section icon={Type} title="Titolo e testo" open={isOpen(i, "body")} onToggle={() => toggleSec(i, "body")}>
                  <div><Lbl extra={<Counter value={em.title} max={LIMITS.title} />}>Titolo</Lbl><Input value={em.title} onChange={(e) => setEmbed(i, { title: e.target.value })} placeholder="Il titolo in grassetto" data-testid="embed-title-input" className={inputC} /></div>
                  <div><Lbl>Link del titolo (facoltativo)</Lbl><Input value={em.url} onChange={(e) => setEmbed(i, { url: e.target.value })} placeholder="https://… — rende il titolo cliccabile" className={inputC} /></div>
                  <div><Lbl>Descrizione</Lbl><RichText value={em.description} onChange={(description) => setEmbed(i, { description })} rows={5} max={LIMITS.description} placeholder="Il testo principale. Usa la barra qui sopra per grassetto, elenchi, menzioni…" testId="embed-description-input" /></div>
                </Section>

                <Section icon={Columns3} title="Campi" badge={em.fields.length || null} open={isOpen(i, "fields")} onToggle={() => toggleSec(i, "fields")}>
                  <p className="text-xs text-[#949BA4]">Piccoli blocchi titolo + testo. «Affiancato» ne mette fino a 3 sulla stessa riga.</p>
                  {em.fields.map((f, fi) => (
                    <div key={fi} className="space-y-2 rounded-lg border border-[#232838] bg-[#0f111a] p-2.5">
                      <div className="flex items-center gap-2">
                        <Input value={f.name} onChange={(e) => setField(i, fi, { name: e.target.value })} placeholder="Titolo del campo" className={`${inputC} flex-1`} />
                        <label className="flex shrink-0 items-center gap-1.5 text-xs text-[#B5BAC1]"><Switch checked={f.inline} onCheckedChange={(inline) => setField(i, fi, { inline })} />Affiancato</label>
                        <Button size="icon" variant="ghost" onClick={() => setEmbed(i, { fields: em.fields.filter((_, j) => j !== fi) })} className="h-8 w-8 shrink-0 text-[#F23F43] hover:bg-[#F23F43]/10"><Trash2 className="h-4 w-4" /></Button>
                      </div>
                      <RichText value={f.value} onChange={(value) => setField(i, fi, { value })} rows={2} compact max={LIMITS.fieldValue} placeholder="Testo del campo" />
                    </div>
                  ))}
                  <Button size="sm" variant="outline" disabled={em.fields.length >= 25} onClick={() => setEmbed(i, { fields: [...em.fields, { name: "", value: "", inline: true }] })} className={outlineBtn}><Plus className="mr-1 h-4 w-4" />Aggiungi campo</Button>
                </Section>

                <Section icon={ImageIcon} title="Immagini" badge={[em.image, em.thumbnail].filter(Boolean).length || null} open={isOpen(i, "img")} onToggle={() => toggleSec(i, "img")}>
                  <div><Lbl>Immagine grande (in fondo)</Lbl><MediaInput value={em.image} onChange={(image) => setEmbed(i, { image })} onVideo={addVideo} /></div>
                  <div><Lbl>Miniatura (in alto a destra)</Lbl><MediaInput value={em.thumbnail} onChange={(thumbnail) => setEmbed(i, { thumbnail })} /></div>
                  <p className="text-xs text-[#949BA4]">Serve un link diretto all'immagine. Trucco: carica l'immagine in un canale Discord, tasto destro → «Copia link».</p>
                </Section>

                <Section icon={User} title="Autore" open={isOpen(i, "author")} onToggle={() => toggleSec(i, "author")}>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div><Lbl>Nome (piccolo, sopra il titolo)</Lbl><Input value={em.author?.name} onChange={(e) => setEmbed(i, { author: { ...em.author, name: e.target.value } })} placeholder={guild?.name || "Staff"} className={inputC} /></div>
                    <div><Lbl>Icona</Lbl><MediaInput compact value={em.author?.iconUrl} onChange={(iconUrl) => setEmbed(i, { author: { ...em.author, iconUrl } })} placeholder="Carica o incolla un link" /></div>
                  </div>
                  {guild?.icon && <Button size="sm" variant="outline" onClick={() => setEmbed(i, { author: { name: em.author?.name || guild.name, iconUrl: guild.icon } })} className={outlineBtn}>Usa nome e icona del server</Button>}
                </Section>

                <Section icon={PanelBottom} title="Piè di pagina" open={isOpen(i, "footer")} onToggle={() => toggleSec(i, "footer")}>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div><Lbl extra={<Counter value={em.footer?.text} max={LIMITS.footer} />}>Testo</Lbl><Input value={em.footer?.text} onChange={(e) => setEmbed(i, { footer: { ...em.footer, text: e.target.value } })} placeholder="Testo piccolo in fondo" className={inputC} /></div>
                    <div><Lbl>Icona</Lbl><MediaInput compact value={em.footer?.iconUrl} onChange={(iconUrl) => setEmbed(i, { footer: { ...em.footer, iconUrl } })} placeholder="Carica o incolla un link" /></div>
                  </div>
                  <label className="flex items-center gap-2 text-sm text-[#B5BAC1]"><Switch checked={em.timestamp} onCheckedChange={(v) => setEmbed(i, { timestamp: v })} />Mostra data e ora di invio</label>
                </Section>
              </div>
            </div>
          ))}
          <Button variant="outline" disabled={embeds.length >= 10} onClick={() => { setEmbeds([...embeds, emptyEmbed()]); setOpenSec((o) => ({ ...o, [`${embeds.length}-body`]: true })); }}
            className="w-full border-dashed border-[#2d3347] bg-transparent py-6 text-[#B5BAC1] hover:bg-[#252B3D] hover:text-white"><Plus className="mr-2 h-4 w-4" />Aggiungi embed {embeds.length > 0 && `(${embeds.length}/10)`}</Button>

          {/* Bottoni */}
          <div className="card-elevated space-y-3 p-4">
            <div className="flex items-center gap-2">
              <MousePointerClick className="h-4 w-4 text-[#8ea0ff]" />
              <span className="flex-1 text-sm font-bold text-white">Bottoni</span>
              <Button size="sm" variant="outline" disabled={buttons.length >= 25} onClick={() => setButtons([...buttons, { label: "", action: "link", style: "primary", url: "", roleId: "", customId: "", emoji: "" }])} className={outlineBtn}><Plus className="mr-1 h-4 w-4" />Bottone</Button>
            </div>
            {buttons.length === 0 && <p className="text-sm text-[#949BA4]">Aggiungi bottoni che aprono un link, danno un ruolo o aprono un ticket.</p>}
            {buttons.map((b, i) => (
              <div key={i} className="space-y-2 rounded-lg border border-[#232838] bg-[#131620] p-3">
                <div className="flex gap-2">
                  <Input value={b.emoji} onChange={(e) => setBtn(i, { emoji: e.target.value })} placeholder="😀" className={`${inputC} w-14 text-center`} />
                  <Input value={b.label} onChange={(e) => setBtn(i, { label: e.target.value })} placeholder="Testo del bottone" maxLength={80} className={`${inputC} flex-1`} />
                  <Button size="icon" variant="ghost" onClick={() => setButtons(buttons.filter((_, j) => j !== i))} className="text-[#F23F43] hover:bg-[#F23F43]/10"><Trash2 className="h-4 w-4" /></Button>
                </div>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <Select value={b.action} onValueChange={(v) => setBtn(i, { action: v })}>
                    <SelectTrigger className={inputC}><SelectValue /></SelectTrigger>
                    <SelectContent className="border-[#232838] bg-[#161923] text-[#DBDEE1]">{buttonActions.map((a) => <SelectItem key={a.value} value={a.value} className="focus:bg-[#252B3D]">{a.label}</SelectItem>)}</SelectContent>
                  </Select>
                  {b.action !== "link" ? (
                    <div className="flex gap-1.5">
                      {buttonStyles.map((s) => (
                        <button key={s.value} type="button" title={s.label} onClick={() => setBtn(i, { style: s.value })}
                          className={`h-9 flex-1 rounded-md border-2 ${b.style === s.value ? "border-white" : "border-transparent"}`}
                          style={{ background: { primary: "#5865F2", secondary: "#4E5058", success: "#248046", danger: "#DA373C" }[s.value] || "#4E5058" }} />
                      ))}
                    </div>
                  ) : <p className="flex items-center text-xs text-[#949BA4]">I bottoni link sono sempre grigi (regola di Discord).</p>}
                </div>
                {b.action === "link" && <Input value={b.url} onChange={(e) => setBtn(i, { url: e.target.value })} placeholder="https://…" className={inputC} />}
                {b.action === "role" && <RoleSelect value={b.roleId} onChange={(v) => setBtn(i, { roleId: v })} />}
                {b.action === "custom" && <Input value={b.customId} onChange={(e) => setBtn(i, { customId: e.target.value })} placeholder="custom-id" className={inputC} />}
              </div>
            ))}
          </div>
        </div>

        {/* ── Anteprima + invio ── */}
        <div className="min-w-0 space-y-4 xl:sticky xl:top-4 xl:self-start">
          <div className="card-elevated p-4">
            <div className="mb-3 flex items-center justify-between">
              <p className="text-xs font-bold uppercase tracking-wide text-[#B5BAC1]">Anteprima</p>
              <span className={`text-[11px] font-semibold ${total > LIMITS.total ? "text-[#F23F43]" : "text-[#6b7185]"}`}>{total}/{LIMITS.total} caratteri</span>
            </div>
            <DiscordEmbedPreview message={message} channelName={channelId ? channelName(channelId) : undefined} fillPlaceholders={false} />
          </div>

          <div className="card-elevated space-y-3 p-4">
            <div className="flex rounded-lg bg-[#0f111a] p-1">
              {[{ v: false, l: "Nuovo messaggio", I: Send }, { v: true, l: "Modifica esistente", I: Pencil }].map(({ v, l, I }) => (
                <button key={l} type="button" onClick={() => setEditMode(v)} data-testid={v ? "edit-mode-switch" : undefined}
                  className={`flex flex-1 items-center justify-center gap-1.5 rounded-md py-1.5 text-sm font-semibold transition-colors ${editMode === v ? "bg-[#5865F2] text-white" : "text-[#949BA4] hover:text-white"}`}><I className="h-3.5 w-3.5" />{l}</button>
              ))}
            </div>
            {editMode ? (
              <div>
                <Lbl>Link del messaggio da modificare</Lbl>
                <Input value={link} onChange={(e) => onLink(e.target.value)} placeholder="https://discord.com/channels/…" data-testid="message-id-input" className={inputC} />
                <p className="mt-1 text-xs text-[#949BA4]">Su Discord: tasto destro sul messaggio del bot → «Copia link del messaggio». Si possono modificare solo i messaggi inviati dal bot.</p>
                {messageId && channelId && <p className="mt-1 text-xs text-[#57F287]">✓ Messaggio in #{channelName(channelId)}</p>}
              </div>
            ) : (
              <div>
                <Lbl>Canale</Lbl>
                <ChannelSelect value={channelId} onChange={setChannelId} types={["text", "announcement"]} testId="destination-channel-select" />
              </div>
            )}
            <Button onClick={send} disabled={sending} data-testid="embed-send-button" className={`w-full ${primaryBtn}`}>
              {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : editMode ? <Pencil className="mr-2 h-4 w-4" /> : <Send className="mr-2 h-4 w-4" />}
              {editMode ? "Aggiorna il messaggio" : "Invia su Discord"}
            </Button>
            <button type="button" onClick={copyJson} className="w-full text-center text-xs font-semibold text-[#949BA4] hover:text-white">Copia come JSON</button>
          </div>
        </div>
      </div>

      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="border-[#232838] bg-[#161923] text-[#DBDEE1]">
          <DialogHeader><DialogTitle className="text-white">Salva come modello</DialogTitle><DialogDescription className="text-[#949BA4]">Lo ritrovi in «I miei modelli» per riusarlo quando vuoi.</DialogDescription></DialogHeader>
          <Input value={tmplName} onChange={(e) => setTmplName(e.target.value)} placeholder="Nome del modello" data-testid="template-name-input" className={inputC} />
          <DialogFooter><Button onClick={doSave} className={primaryBtn} data-testid="template-save-confirm"><Save className="mr-2 h-4 w-4" />Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={loadOpen} onOpenChange={setLoadOpen}>
        <DialogContent className="border-[#232838] bg-[#161923] text-[#DBDEE1]">
          <DialogHeader><DialogTitle className="text-white">I miei modelli</DialogTitle></DialogHeader>
          <div className="max-h-80 space-y-2 overflow-y-auto">
            {tmpls.length === 0 && <p className="text-sm text-[#949BA4]">Nessun modello salvato. Crea un messaggio e premi «Salva modello».</p>}
            {tmpls.map((t) => (
              <div key={t.id} className="flex items-center gap-2 rounded-lg bg-[#131620] px-3 py-2">
                <span className="h-8 w-1 rounded-full" style={{ background: t.payload?.embeds?.[0]?.color || "#5865F2" }} />
                <span className="flex-1 font-semibold text-white">{t.name}</span>
                <Button size="sm" onClick={() => applyTmpl(t)} className={primaryBtn}>Carica</Button>
                <Button size="icon" variant="ghost" onClick={async () => { await templates.remove(t.id); loadTmpls(); }} className="text-[#F23F43] hover:bg-[#F23F43]/10"><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
